import { useState } from 'react';
import { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice } from '../MissionShell/MissionShell';
import { LogSummary, refreshMis } from '../../api/batch5Api';
import { useFileRun } from '../../hooks/useFileRun';
import type { FileResult } from '../../api/files';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  "Upload the MIS template (this month's workbook, or last month's derived output). It holds the outlet tabs, formulas and charts.",
  'Upload this month\'s source files: Profitability Report, Data New, Data, Tender and Agregators Commission (required); Rent and Last Year Sales Temp (optional, only if changed).',
  'Click Refresh MIS Workbook. Review the match confidence in the log before trusting the output.',
];

interface PanelProps {
  mission: Mission;
  onBack: () => void;
}

function MisRefreshPanel({ mission, onBack }: PanelProps) {
  const [master, setMaster] = useState<File[]>([]);
  const [sources, setSources] = useState<File[]>([]);
  const { status, error, result, fail, run } = useFileRun<FileResult<LogSummary>>();

  const handleSubmit = async () => {
    if (master.length === 0 || sources.length === 0) {
      fail('Upload the MIS template and at least one source file.');
      return;
    }
    await run(() => refreshMis(master[0], sources), 'Refresh failed.');
  };

  return (
    <MissionShell mission={mission} onBack={onBack} steps={STEPS} wide>
      <div className="mb-5 rounded-lg border border-border bg-muted p-4">
        <p className="mb-2 text-sm font-semibold text-foreground">📝 How this works</p>
        <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          <li>Each source file is matched to its sheet by content (header overlap) plus file name similarity.</li>
          <li>Only matched data-feed sheets are rewritten; outlet tabs, formulas, charts and formats are left untouched.</li>
          <li>A sheet with no matching upload keeps what the template already has (e.g. last month's Rent).</li>
          <li>Excel recalculates every formula when the file is opened.</li>
        </ul>
      </div>

      <FileDrop
        label="MIS template"
        required
        accept=".xlsx,.xlsm"
        hint="XLSX or XLSM"
        accentColor={mission.color}
        files={master}
        onFiles={setMaster}
      />
      <FileDrop
        label="Source files"
        note="5 required, 2 optional (see How to use)."
        required
        multiple
        accept=".xlsx,.xlsm"
        hint="XLSX or XLSM"
        accentColor={mission.color}
        files={sources}
        onFiles={setSources}
      />

      <Button
        onClick={handleSubmit}
        disabled={status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Refreshing…' : 'Refresh MIS Workbook'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Refreshing…'} />}

      {status === 'error' && error && <ErrorNotice message={error} />}
      {status === 'success' && result && (
        <div className="mt-4 space-y-3 text-sm">
          <div className="rounded-lg bg-green-50 px-4 py-3 text-green-700">✅ {result.filename} downloaded.</div>
          {result.summary?.log && (
            <details open className="rounded-lg border border-border bg-muted px-4 py-3 text-muted-foreground">
              <summary className="cursor-pointer text-foreground">
                Discovery &amp; processing log: review match confidence before trusting the output
              </summary>
              <pre className="mt-2 whitespace-pre-wrap text-xs">{result.summary.log}</pre>
            </details>
          )}
        </div>
      )}
      {status === 'idle' && <InfoNotice>Upload the MIS template and at least one source file to enable the refresh.</InfoNotice>}
    </MissionShell>
  );
}

export default MisRefreshPanel;
